/**
 * Predictive Search Component for Custom Header
 * Provides real-time search suggestions as the user types
 * Standalone version that doesn't depend on theme framework
 */
class PredictiveSearchCustom extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.results = this.querySelector('[data-predictive-search-results]');
    this.abortController = null;
    this.cachedResults = new Map();
  }

  connectedCallback() {
    if (!this.input || !this.results) return;

    // Input event with debounce
    this.input.addEventListener('input', this.debounce(this.onInput.bind(this), 300));

    // Focus shows results if there are any
    this.input.addEventListener('focus', () => {
      if (this.results.innerHTML.trim() !== '') {
        this.showResults();
      }
    });

    // Click outside closes results
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.hideResults();
      }
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', this.onKeydown.bind(this));

    // Form submit
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => {
        const query = this.input.value.trim();
        if (!query) {
          e.preventDefault();
        }
      });
    }
  }

  /**
   * Handle input changes
   */
  async onInput() {
    const query = this.input.value.trim();

    // Clear if query too short
    if (query.length < 2) {
      this.results.innerHTML = '';
      this.hideResults();
      return;
    }

    // Check cache first
    if (this.cachedResults.has(query)) {
      this.renderResults(this.cachedResults.get(query));
      return;
    }

    // Show loading state
    this.showLoading();

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${rootUrl}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,article,page&resources[limit]=6&resources[options][unavailable_products]=last`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        throw new Error('Search request failed');
      }

      const data = await response.json();

      // Cache the results
      this.cachedResults.set(query, data);

      // Limit cache size
      if (this.cachedResults.size > 20) {
        const firstKey = this.cachedResults.keys().next().value;
        this.cachedResults.delete(firstKey);
      }

      this.renderResults(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Predictive search error:', error);
        this.showError();
      }
    }
  }

  /**
   * Render search results
   */
  renderResults(data) {
    const resources = data.resources?.results;
    if (!resources) {
      this.showNoResults();
      return;
    }

    const products = resources.products || [];
    const collections = resources.collections || [];
    const articles = resources.articles || [];
    const pages = resources.pages || [];

    const hasResults = products.length > 0 || collections.length > 0 || articles.length > 0 || pages.length > 0;

    if (!hasResults) {
      this.showNoResults();
      return;
    }

    let html = '';

    // Products
    if (products.length > 0) {
      html += '<div class="predictive-search__section">';
      html += '<div class="predictive-search__section-title">Products</div>';
      products.forEach(product => {
        const image = product.featured_image?.url || product.image || '';
        const price = this.formatMoney(product.price);
        html += `
          <a href="${product.url}" class="predictive-search__item">
            ${image ? `<img src="${this.resizeImage(image, '100x100')}" alt="${this.escapeHtml(product.title)}" width="50" height="50" loading="lazy">` : '<div class="predictive-search__item-placeholder"></div>'}
            <div class="predictive-search__item-content">
              <span class="predictive-search__item-title">${this.escapeHtml(product.title)}</span>
              <span class="predictive-search__item-price">${price}</span>
            </div>
          </a>
        `;
      });
      html += '</div>';
    }

    // Collections
    if (collections.length > 0) {
      html += '<div class="predictive-search__section">';
      html += '<div class="predictive-search__section-title">Collections</div>';
      collections.forEach(collection => {
        html += `
          <a href="${collection.url}" class="predictive-search__item predictive-search__item--collection">
            <span class="predictive-search__item-title">${this.escapeHtml(collection.title)}</span>
          </a>
        `;
      });
      html += '</div>';
    }

    // Articles
    if (articles.length > 0) {
      html += '<div class="predictive-search__section">';
      html += '<div class="predictive-search__section-title">Articles</div>';
      articles.forEach(article => {
        html += `
          <a href="${article.url}" class="predictive-search__item predictive-search__item--article">
            <span class="predictive-search__item-title">${this.escapeHtml(article.title)}</span>
          </a>
        `;
      });
      html += '</div>';
    }

    // View all link
    const query = this.input.value.trim();
    const rootUrl = window.Shopify?.routes?.root || '/';
    html += `
      <a href="${rootUrl}search?q=${encodeURIComponent(query)}" class="predictive-search__view-all">
        View all results for "${this.escapeHtml(query)}"
      </a>
    `;

    this.results.innerHTML = html;
    this.showResults();
  }

  /**
   * Show loading state
   */
  showLoading() {
    this.results.innerHTML = '<div class="predictive-search__loading">Searching...</div>';
    this.showResults();
  }

  /**
   * Show no results message
   */
  showNoResults() {
    const query = this.input.value.trim();
    this.results.innerHTML = `<div class="predictive-search__no-results">No results found for "${this.escapeHtml(query)}"</div>`;
    this.showResults();
  }

  /**
   * Show error message
   */
  showError() {
    this.results.innerHTML = '<div class="predictive-search__no-results">Something went wrong. Please try again.</div>';
    this.showResults();
  }

  /**
   * Show results dropdown
   */
  showResults() {
    this.results.hidden = false;
  }

  /**
   * Hide results dropdown
   */
  hideResults() {
    this.results.hidden = true;
  }

  /**
   * Handle keyboard navigation
   */
  onKeydown(e) {
    const items = this.results.querySelectorAll('.predictive-search__item, .predictive-search__view-all');
    if (items.length === 0) return;

    const currentIndex = Array.from(items).findIndex(item => item === document.activeElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex]?.focus();
        break;

      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex]?.focus();
        break;

      case 'Escape':
        this.hideResults();
        this.input.focus();
        break;
    }
  }

  /**
   * Format money based on Shopify settings
   */
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const value = (parseInt(cents, 10) / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'AUD';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency
    }).format(value);
  }

  /**
   * Resize Shopify image URL
   */
  resizeImage(url, size) {
    if (!url) return '';
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/\.([^.]+)$/, `_${size}.$1`);
    }
    return url;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Debounce function
   */
  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }
}

// Define custom element
if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearchCustom);
}
